import { RequestParams } from '~/Api/generated-api/api';

export type ToolRoutePolicyClientOptions<TParams> = {
  isFeatureEnabled: () => boolean;
  loadPolicyManaged: (params: TParams) => Promise<boolean>;
};

export class ToolRoutePolicyClient {
  private policyManaged: boolean | null = null;

  private pendingPolicyManaged: Promise<boolean> | null = null;

  private resetVersion = 0;

  constructor(
    private readonly options: ToolRoutePolicyClientOptions<RequestParams>,
  ) {}

  async shouldUsePolicyRoutes(params: RequestParams): Promise<boolean> {
    if (!this.isPolicyRoutingEnabled()) {
      return false;
    }

    return this.loadCachedPolicyManaged(params);
  }

  reset(): void {
    this.resetVersion += 1;
    this.policyManaged = null;
    this.pendingPolicyManaged = null;
  }

  private isPolicyRoutingEnabled(): boolean {
    return this.options.isFeatureEnabled();
  }

  private loadCachedPolicyManaged(params: RequestParams): Promise<boolean> {
    if (this.policyManaged !== null) {
      return Promise.resolve(this.policyManaged);
    }

    if (!this.pendingPolicyManaged) {
      this.pendingPolicyManaged = this.createPolicyManagedRequest(params);
    }

    return this.pendingPolicyManaged;
  }

  private createPolicyManagedRequest(params: RequestParams): Promise<boolean> {
    const loadVersion = this.resetVersion;

    return this.options
      .loadPolicyManaged(params)
      .then((policyManaged) => policyManaged === true)
      .catch(() => false)
      .then((policyManaged) =>
        this.rememberPolicyManaged(loadVersion, policyManaged),
      );
  }

  private rememberPolicyManaged(
    loadVersion: number,
    policyManaged: boolean,
  ): boolean {
    if (this.resetVersion === loadVersion) {
      this.policyManaged = policyManaged;
      this.pendingPolicyManaged = null;
    }

    return policyManaged;
  }
}
